import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { extractErrorMessage } from '@/lib/errors';
import { cn } from '@/lib/utils';
import { useState } from 'react';

interface SelectOption<T extends string> {
  value: T;
  label: string;
}

interface InlineSelectFieldProps<T extends string> {
  value: T | null;
  options: SelectOption<T>[];
  onSave: (next: T | null) => Promise<void> | void;
  placeholder?: string;
  emptyLabel?: string;
  clearable?: boolean;
  disabled?: boolean;
  className?: string;
}

const CLEAR_VALUE = '__clear__';

export function InlineSelectField<T extends string>({
  value,
  options,
  onSave,
  placeholder = 'Select…',
  emptyLabel = '—',
  clearable = true,
  disabled = false,
  className,
}: InlineSelectFieldProps<T>) {
  const [error, setError] = useState<string | null>(null);
  const [isSaving, setIsSaving] = useState(false);

  const handleChange = async (raw: string) => {
    setError(null);
    const next = raw === CLEAR_VALUE ? null : (raw as T);
    if (next === value) return;
    setIsSaving(true);
    try {
      await onSave(next);
    } catch (err: unknown) {
      setError(extractErrorMessage(err, 'Failed to save'));
    } finally {
      setIsSaving(false);
    }
  };

  const selected = value ? options.find((o) => o.value === value) : null;

  return (
    <div className={cn('space-y-1', className)}>
      <Select
        value={value ?? undefined}
        onValueChange={handleChange}
        disabled={disabled || isSaving}
      >
        <SelectTrigger
          className={cn(
            'h-9 text-sm',
            !selected && 'text-muted-foreground',
            'hover:bg-muted/50'
          )}
        >
          <SelectValue placeholder={placeholder}>
            {selected?.label ?? emptyLabel}
          </SelectValue>
        </SelectTrigger>
        <SelectContent>
          {options.map((opt) => (
            <SelectItem key={opt.value} value={opt.value}>
              {opt.label}
            </SelectItem>
          ))}
          {clearable && value !== null ? (
            <SelectItem value={CLEAR_VALUE} className="text-muted-foreground">
              Clear
            </SelectItem>
          ) : null}
        </SelectContent>
      </Select>
      {error ? <p className="text-xs text-destructive">{error}</p> : null}
    </div>
  );
}

